import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Banner from "../../components/Banner";
import BannerSlider from "../../components/BannerSlider";
import GameCards from "../../pages/Parts/Home/GameCards";
import { Link, useParams, useLocation } from "react-router-dom";
import { useActiveGroups } from "hooks/useGameEngine";
import GroupPage from "../../pages/GroupPage";
import DynamicGroupSwiper from "../../components/DynamicGroupSwiper";
import { useGameEngine, getGameImage } from "hooks/useGameEngine";
import GameImage from '../../components/GameImage';
import * as LucideIcons from "lucide-react";

const SportsHome = ({ t, groups }) => {
  const { slug } = useParams();
  const location = useLocation();
  const selectedSport = slug || "all";

  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [visibleSwipers, setVisibleSwipers] = useState(3);
  const [phase, setPhase] = useState("idle");
  const searchTimeoutRef = React.useRef(null);

  useEffect(() => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    setSearchQuery("");
    setTempSearchQuery("");
    setVisibleSwipers(3);
  }, [slug]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setSearchQuery('');
      setPhase('idle');
      return;
    }

    setPhase('loading');
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value.trim());
      setPhase('results');
    }, 600);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter" && searchQuery !== tempSearchQuery) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      setSearchQuery(tempSearchQuery);
      setPhase('results');
    }
  };

  const dynamicTabs = (groups || [])
    .filter(g => g.show_as_tab_sports)
    .map(g => ({
      key: g.slug || g.id,
      label: g.name,
      icon: g.icon,
      iconClass: "mdi mdi-gamepad-variant",
      path: `/sports/${g.slug || g.id}`
    }));

  const allCategories = [
    { key: "all", label: "Lobby", icon: "lobby.svg", path: "/sports" },
    ...dynamicTabs
  ];

  return (
    <div>
      <Helmet>
        <title>Sports Bet</title>
        <link rel="canonical" href="/sports" />
      </Helmet>

      {/* Shared Banner */}
      <BannerSlider type="sports" />
      <GameCards />

      <div className="casino-container">
        {/* Search Bar matching Casino style */}
        <div className="search-container">
          <img
            src="/assets/bcimages/Casino/svgexport-1 (1).svg"
            alt="search"
            className="search-icon"
          />
          <input
            type="text"
            value={tempSearchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyPress}
            placeholder="Search sports"
            className="search-input"
          />
        </div>

        {/* Filter Tabs matching Casino style */}
        <div className="filter-buttons mt-5">
          {allCategories.map(({ key, label, iconClass, icon, path }) => {
            const isActive = String(selectedSport) === String(key);
            const isLucideIcon = typeof icon === 'string' && LucideIcons[icon];
            return (
              <Link key={key} to={path} style={{ textDecoration: "none" }}>
                <div className={`filter-button ${isActive ? "active" : ""}`}>
                  {isLucideIcon ? (
                    React.createElement(LucideIcons[icon], {
                      size: 16
                    })
                  ) : icon ? (
                    <img src={`/assets/bcimages/Casino/${icon}`} className="filter-btn-img" />
                  ) : (
                    <i className={iconClass}></i>
                  )}
                  {label}
                </div>
              </Link>
            );
          })}
        </div>

        {/* Sports / Group Content */}
        <div className="mt-4">
          {selectedSport !== "all" ? (
            <GroupPage groupSlug={selectedSport} hideBanner={true} isLobbyTab={true} lobbySearchQuery={searchQuery} />
          ) : (
            <div style={{ marginTop: "40px" }}>
              {phase === 'loading' && (
                <div style={{ display: 'grid', placeItems: 'center', padding: '80px 0' }}>
                  <div style={{ width: '48px', height: '6px', backgroundColor: '#0f212e', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: '33%', backgroundColor: 'rgba(255,255,255,0.7)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  </div>
                </div>
              )}
              {phase !== 'loading' && (
                <>
                  {searchQuery ? (
                    <div>
                      <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold', margin: '20px 0' }}>
                        Search Results for "{searchQuery}"
                      </div>
                      <GlobalSearchResults searchQuery={searchQuery} />
                    </div>
                  ) : (
                    <>
                      {(groups || [])
                        .filter((group) => group.show_on_home_sports)
                        .slice(0, visibleSwipers)
                        .map((group) => (
                          <DynamicGroupSwiper
                            key={group.id}
                            group={group}
                            prefix="/sports/group/"
                          />
                        ))}
                    </>
                  )}

                  {(groups || []).filter((group) => group.show_on_home_sports).length > visibleSwipers && !searchQuery && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      marginTop: '32px',
                      marginBottom: '32px',
                      width: '100%',
                      padding: '0 20px'
                    }}>
                      <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.05)' }} />
                      <button
                        onClick={() => setVisibleSwipers(prev => prev + 3)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#9ca3af',
                          fontSize: '13px',
                          fontWeight: '900',
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          transition: 'color 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.color = '#ffffff'}
                        onMouseOut={(e) => e.target.style.color = '#9ca3af'}
                      >
                        LOAD MORE
                      </button>
                      <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.05)' }} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function GlobalSearchResults({ searchQuery }) {
  const { data: games, isLoading } = useGameEngine({ search: searchQuery, limit: 100 });

  if (isLoading) return <div style={{ color: '#94a6b5' }}>Searching...</div>;
  if (!games || games.length === 0) return <div style={{ color: '#ff5a7a' }}>No games found.</div>;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: '12px'
    }}>
      {games.map((g, i) => (
        <div key={i} style={{ backgroundColor: '#292d2e', borderRadius: '8px', overflow: 'hidden', position: 'relative', aspectRatio: '3/4' }}>
          <a href={`/play/${g.id}`}>
            <GameImage src={getGameImage(g)} alt={g.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </a>
        </div>
      ))}
    </div>
  );
}

export function SportsHomeWrapper(props) {
  const { data: groups } = useActiveGroups();
  return <SportsHome {...props} groups={groups || []} />;
}

export default SportsHomeWrapper;
